import React, { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import Input from '../UI/Input'
import Button from '../UI/Button'

const MIN_PASSWORD_LENGTH = 8

const ResetPassword = () => {
    const [params] = useSearchParams()
    const navigate = useNavigate()
    const token = params.get('token')

    const [password, setPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')
    const [message, setMessage] = useState('')
    const [error, setError] = useState(null)
    const [isSubmitting, setIsSubmitting] = useState(false)

    useEffect(() => {
        if (!token) {
            setError('Missing or invalid password reset token.')
        }
    }, [token])

    const validatePassword = (pwd) => {
        if (pwd.length < MIN_PASSWORD_LENGTH) {
            return `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`
        }
        if (!/[A-Z]/.test(pwd)) return 'Password must include at least one uppercase letter.'
        if (!/[a-z]/.test(pwd)) return 'Password must include at least one lowercase letter.'
        if (!/[0-9]/.test(pwd)) return 'Password must include at least one number.'
        if (!/[!@#$%^&*]/.test(pwd)) return 'Password must include at least one special character.'
        return null
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        setMessage('')
        setError(null)

        const trimmedPassword = password.trim()
        const trimmedConfirm = confirmPassword.trim()

        const validationError = validatePassword(trimmedPassword)
        if (validationError) {
            setError(validationError)
            return
        }

        if (trimmedPassword !== trimmedConfirm) {
            setError('Passwords do not match.')
            return
        }

        if (!token) {
            setError('Invalid or missing token.')
            return
        }

        setIsSubmitting(true)

        try {
            const response = await fetch(`${import.meta.env.VITE_BACKEND_URL}/auth/reset-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    token,
                    password: trimmedPassword,
                    confirmPassword: trimmedConfirm,
                }),
            })

            const data = await response.json().catch(() => ({}))

            if (response.ok) {
                setMessage('Password reset successful. You can now log in.')
                setPassword('')
                setConfirmPassword('')
            } else {
                setError(data.error || 'Reset failed. Please try again.')
            }
        } catch (err) {
            setError('Network or server error. Please try again later.')
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <div className="mt-10 max-w-md mx-auto px-4">
            <h2 className="text-2xl font-bold mb-4 text-center">Reset Password</h2>
            {error && <p className="text-error mb-3 text-sm">{error}</p>}
            {message && <p className="text-success mb-3 text-sm">{message}</p>}

            <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                    id="password"
                    type="password"
                    label="New Password"
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter new password"
                    disabled={isSubmitting}
                    required
                />
                <Input
                    id="confirmPassword"
                    type="password"
                    label="Confirm Password"
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    disabled={isSubmitting}
                    required
                />
                <Button type="submit" size="lg" loading={isSubmitting} className="w-full">
                    Reset Password
                </Button>
            </form>

            <div className="text-xs text-gray-500 mt-4">
                Password must be at least {MIN_PASSWORD_LENGTH} characters and include a mix of uppercase,
                lowercase, numbers, and symbols.
            </div>
        </div>
    )
}

export default ResetPassword
